import * as Yup from 'yup';
import { isWithinInterval, parseISO, isSameDay, format, isBefore, isAfter, addDays, isFuture } from 'date-fns';
import { mustRenewAutoLimit, flatCancelReasons } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { EFFECTIVE_DATE_LOCKED_MESSAGE } from '@ourbranch/policy-utils';

import { PolicyStatus, PolicyChangeAction } from 'core/helpers/policy-status';
import { requiredString, addressBlock } from 'common/helpers/yup-helpers';
import { getEarliestDateAgentAllowedToBackdateCancellation } from 'customer/components/policy/details/helpers/backdate-cancellation';
import { getFarmInterestedPartyItemSchema } from 'common/components/farm/additional-party/add-farm-interested-party.validation';
import { screenActionFor } from './screen-action';

Yup.addMethod(Yup.string, 'requiredString', requiredString);

/* FYI
 * address node is already validated as required in the add-interested-party-form.js
 * we don't want to add required again here, because we can load up a policy with an address as null,
 * and we should allow the policy modification to go through without this validation blocking it
 */
// Auto Bill to other party (CFM, #36200): its invoices are emailed, so the email is required (as in the
// add form, the offer schema and farm), and a policy has at most one.
export const lienHolderValidation = Yup.array(
  Yup.object().shape({
    name: Yup.string().required('Lienholder name or company is required'),
    email: Yup.string()
      .email('Please use a valid email')
      .nullable()
      .when('relationship', {
        is: 'billToOther',
        then: Yup.string().email('Please use a valid email').required('Email is required for bill-to-other').nullable()
      }),
    address: Yup.object()
      .shape({
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        city: Yup.string().nullable(),
        state: Yup.string().nullable(),
        zip: Yup.string().nullable()
      })
      .nullable(),
    loanNumber: Yup.string().nullable(),
    VIN: Yup.string().nullable(),
    // #36264: brand-typed parties carry their vehicles as a list and, for a Trustee, the trustee names.
    VINs: Yup.array().of(Yup.string()).nullable(),
    trusteeNames: Yup.string().nullable()
  })
)
  .nullable()
  .test(
    'singleBillToOther',
    'Only one Bill to other party is allowed',
    (parties) => (parties || []).filter((party) => party?.relationship === 'billToOther').length <= 1
  );

const partiesValidation = Yup.array(
  Yup.object().shape({
    name: Yup.string().requiredString('Additional party name or company is required'),
    email: Yup.string().email('Please use a valid email').nullable(),
    relationship: Yup.string().requiredString('Additional party relationship is required'),
    address: addressBlock('Additional party address').nullable()
  })
).nullable();

const farmPartiesValidation = Yup.array(
  Yup.object()
    .shape({
      name: Yup.string().requiredString('Farm interested party name or company is required'),
      relationship: Yup.string().requiredString('Farm interested party relationship is required'),
      address: Yup.object()
        .shape({
          address: Yup.string().nullable(),
          address2: Yup.string().nullable(),
          city: Yup.string().nullable(),
          state: Yup.string().nullable(),
          zip: Yup.string().nullable()
        })
        .nullable()
    })
    .concat(getFarmInterestedPartyItemSchema(true))
).nullable();

const additionalPartiesValidations = {
  A: lienHolderValidation,
  H: partiesValidation
};

const today = format(new Date(), 'yyyy-MM-dd');

const CANCEL_REASON_REQUIRED_MESSAGE = 'Please select the reason for cancelling.';

export const validationSchema = ({
  policyType,
  minDate,
  maxDate,
  values,
  wasEffectiveDateUpdated,
  policyStatus,
  term,
  agentCanBackDate
}) => {
  const effectiveDateSchema = Yup.object().shape({
    effectiveDate: Yup.string().test('invalidEffectiveDate', 'Invalid Effective Date', function test(value) {
      const valueParsed = parseISO(value);

      // reference getStartMinMaxDate function for min/max date logic
      if (term === 1) {
        const effectiveDateWithinValidRange =
          isWithinInterval(valueParsed, { start: minDate, end: maxDate }) ||
          isSameDay(valueParsed, minDate) ||
          isSameDay(valueParsed, maxDate);

        if (!effectiveDateWithinValidRange) {
          return this.createError({
            message: `Effective date must be between ${format(minDate, 'MM/dd/yyyy')} and ${format(
              maxDate,
              'MM/dd/yyyy'
            )}`
          });
        }
      }

      // The effective date is LOCKED once the policy has become effective (#36450): the policy has been
      // reported (written premium, for one) in the period containing that date, and moving it afterwards
      // makes the published numbers stop tying out. Future-effective is the only status under which the
      // date has not arrived; Active, Inactive (expired) and cancelled policies were all reported. No
      // permission unlocks it — cancel with reason "Policy Rewrite" and use Rewrite Policy instead.
      // policy-mod's validateEvent enforces the same rule server-side; this is the courteous copy.
      // Rationale: @ourbranch/policy-utils effective-date-lock.ts, docs/policy-effective-date-immutable.md.
      const effectiveDateLocked = policyStatus !== PolicyStatus.Future;

      if (wasEffectiveDateUpdated && effectiveDateLocked) {
        return this.createError({ message: EFFECTIVE_DATE_LOCKED_MESSAGE });
      }

      return true;
    })
  });
  // validate end date and cancel reason, if you add validation for endDate, you also need to add it for transactionDate
  if (values.cancel) {
    return Yup.object().shape({
      cancelEffectiveDate: Yup.string()
        // can only have cancelEffectiveDate OR transactionDate, so ignore if transactionDate present
        // cancelEffectiveDate = endDate +1 and behaves like endDate, so most of this validation will be similar, but with dates increased by 1
        .test(
          'cancelEffectiveDate_before_effectiveDate',
          'You cannot set the cancel date prior to the policy’s effective date.',
          function test(value) {
            if (this.parent.transactionDate) {
              return true;
            }
            return new Date(this.parent.effectiveDate) <= new Date(value);
          }
        )
        .test(
          'cannot_backdate',
          `Cancel effective date cannot be more than 60 days before today's date. If you'd like to cancel the policy more than 60 days in the past, please reach out to support with appropriate documentation.`,
          function test(value) {
            if (this.parent.transactionDate) {
              return true;
            }
            const minDateForCancellation = getEarliestDateAgentAllowedToBackdateCancellation(
              this.parent.effectiveDate,
              agentCanBackDate
            );

            return (
              agentCanBackDate ||
              flatCancelReasons.includes(this.parent.cancelReason) ||
              isBefore(new Date(minDateForCancellation), new Date(value)) ||
              isSameDay(new Date(minDateForCancellation), new Date(value)) ||
              isFuture(new Date(this.parent.effectiveDate)) // all agents are allowed to cancel future effective policies
            );
          }
        )
        .test(
          'cancelEffectiveDate_past_fullTermPolicyEndDate',
          'You cannot cancel the policy after the last full day of coverage.',
          function test(value) {
            if (this.parent.transactionDate) {
              return true;
            }
            // cancelEffectiveDate = endDate +1, so compare to fullTermPolicyEndDate +2 because we want to include full term cancels
            const fullTermCancelEffectiveDate = format(
              addDays(parseISO(values.fullTermPolicyEndDate), 2),
              'yyyy-MM-dd'
            );

            return isAfter(parseISO(fullTermCancelEffectiveDate), parseISO(value));
          }
        )
        .test(
          'no_policies_with_one_day_coverage_cancelEffectiveDate',
          'Branch does not support policies with a single day of coverage.',
          function test(value) {
            if (this.parent.transactionDate) {
              return true;
            }
            // cancelEffectiveDate = endDate +1, so compare to effectiveDate +1
            const effectiveDatePlusOne = format(addDays(parseISO(this.parent.effectiveDate), 1), 'yyyy-MM-dd');

            return value !== effectiveDatePlusOne;
          }
        ),
      endDate: Yup.string()
        // ignore if transactionDate present, otherwise, endDate = cancelEffectiveDate -1
        .required('Cancellation end date is required')
        .test(
          'endDate_before_effectiveDate',
          'You cannot set the cancel date prior to the policy’s effective date.',
          function test(value) {
            if (this.parent.transactionDate) {
              return true;
            }
            return new Date(this.parent.effectiveDate) <= new Date(value);
          }
        )
        .test(
          'endDate_past_fullTermPolicyEndDate',
          'You cannot cancel the policy after the last full day of coverage.',
          function test(value) {
            // we want to include full term cancels, so add 1 here to fullTermPolicyEndDate
            if (this.parent.transactionDate) {
              return true;
            }
            const fullTermCancelEffectiveDate = format(
              addDays(parseISO(values.fullTermPolicyEndDate), 1),
              'yyyy-MM-dd'
            );

            return isAfter(parseISO(fullTermCancelEffectiveDate), parseISO(value));
          }
        ),
      transactionDate: Yup.string()
        // can only have cancelEffectiveDate OR transactionDate
        .test(
          'cannot_backdate_transactionDate',
          'You cannot cancel the policy before today’s date.',
          function test(value) {
            if (!value) {
              return true;
            }
            // If agent can't backdate, then the end date must be today or in the future
            return (
              agentCanBackDate ||
              flatCancelReasons.includes(this.parent.cancelReason) ||
              isBefore(new Date(today), new Date(value)) ||
              isSameDay(new Date(today), new Date(value))
            );
          }
        )
        .test(
          'TransactionDate_past_fullTermPolicyEndDate',
          'You cannot cancel the policy after the last full day of coverage.',
          function test(value) {
            if (!value) {
              return true;
            }
            return isAfter(parseISO(values.fullTermPolicyEndDate), parseISO(value));
          }
        ),

      cancelReason: Yup.string().required(CANCEL_REASON_REQUIRED_MESSAGE)
    });
  }

  let baseSchema = effectiveDateSchema.concat(
    Yup.object().shape({
      additionalParties: additionalPartiesValidations[policyType],
      farmInterestedParties: isFarmBasedPolicyType(policyType) ? farmPartiesValidation : Yup.array().nullable()
    })
  );

  // #36446: a shortened end date or a transaction date previews a cancellation (and shows the
  // cancellation form) even while `cancel` is unset, and PolicyCancellationInput.cancelReason is String!.
  if (screenActionFor(values) === PolicyChangeAction.Cancel) {
    baseSchema = baseSchema.concat(
      Yup.object().shape({ cancelReason: Yup.string().required(CANCEL_REASON_REQUIRED_MESSAGE) })
    );
  }

  const limitForCancellingAutoPolicyRenewal = mustRenewAutoLimit[values.state]?.[policyType];
  if (limitForCancellingAutoPolicyRenewal) {
    baseSchema = baseSchema.concat(
      Yup.object().shape({
        autoRenew: Yup.boolean().test(
          'valid',
          `Policies more than ${limitForCancellingAutoPolicyRenewal} terms must be renewed.`,
          function test(value) {
            if (this.parent.term <= limitForCancellingAutoPolicyRenewal) {
              return true;
            }
            return this.parent.term > limitForCancellingAutoPolicyRenewal && value;
          }
        )
      })
    );
  }

  if (values.isNonRenew) {
    return Yup.object().shape({
      nonRenewReason: Yup.mixed().test('non renew reason', 'Required', function test(value) {
        const isNonRenew = this.parent.isNonRenew;
        if (isNonRenew && !value) {
          return this.createError({
            message: 'Please choose a Non-renewal reason'
          });
        }
        return true;
      })
    });
  }

  return baseSchema;
};
